import { useMemo } from 'react';
import { format, subDays } from 'date-fns';
import {
  useAveragePlate,
  useCgmWholeSummary,
  useGoals,
  useMeals,
  useMediterraneanScore,
  useNutritionDetailsByDate,
  usePatient,
  usePatientClinics,
  usePatientReports,
  useSubscriptions,
} from '@/hooks/queries';
import { computeMealTimingKpis } from '@/lib/mealTiming';
import { buildPatientContext, type PatientContext } from '@/lib/patientContext';
import type { AveragePlateData, NutritionDetailsRow } from '@/api/reports';

/**
 * Resolves the standardized RxAssistant patient context for a patient by reading
 * the same queries the rest of the portal already uses. Assembling it here (a
 * hook mounted inside the always-present, flag-gated RxAssistant) means the
 * context data is warmed on patient load and ready the moment the clinician
 * opens the assistant — regardless of which page they're on. See the pure
 * assembler in `@/lib/patientContext` and the "RxAssistant Patient Context"
 * design note.
 *
 * Range: a fixed trailing 30-day window (the app's default range), so the
 * context is consistent no matter where the chat is launched from and shares
 * cache with any page already on the default range.
 *
 * Populates the full standardized context: demographics, motivations, dietary
 * restrictions, clinics, header "details" dates, goals, meal-logging summary,
 * diet quality, average plate, per-day calories + macronutrients, and CGM. All
 * metric fields are compact summaries (counts / averages), never raw meal logs,
 * so the context payload stays small regardless of how much the patient logged.
 * Richer meal-level detail is added on top only on the Logs pages (see the
 * meal-page boost in RxAssistantChat).
 */

const HH_MM = (mins: number | null): string | null =>
  mins === null
    ? null
    : `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

/** Tolerant per-day nutrient sum: `<field>_sum` (flat) or `<field>.sum`
 *  (nested), mirroring the Logs views' extraction. */
const nutrientSum = (row: NutritionDetailsRow, field: string): number => {
  const flat = row[`${field}_sum`];
  if (typeof flat === 'number') return flat;
  const agg = row[field];
  if (agg && typeof agg === 'object' && typeof (agg as { sum?: unknown }).sum === 'number') {
    return (agg as { sum: number }).sum;
  }
  return 0;
};

/** Average of a nutrient across the days that logged any of it. Null when no
 *  day has data. Rounded to a whole number. */
const avgPerDay = (rows: NutritionDetailsRow[], field: string): number | null => {
  let total = 0;
  let days = 0;
  for (const row of rows) {
    const v = nutrientSum(row, field);
    if (v > 0) {
      total += v;
      days += 1;
    }
  }
  return days > 0 ? Math.round(total / days) : null;
};

/** Average-plate `overall` food-group fractions → a plain record, dropping
 *  nulls. Null when there's no overall breakdown. */
const plateRecord = (data: AveragePlateData | null | undefined): Record<string, number> | null => {
  const overall = data?.overall;
  if (!overall) return null;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(overall)) {
    if (typeof v === 'number') out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : null;
};

export type UsePatientContextResult = {
  context: PatientContext;
  /** True while any of the underlying queries are still loading with no cached
   *  data — lets callers avoid attaching a half-empty context. */
  isLoading: boolean;
};

export function usePatientContext(patientId: number): UsePatientContextResult {
  // Fixed trailing 30-day window. Recomputed only on mount; the exact day
  // boundary doesn't need to shift mid-session.
  const range = useMemo(() => {
    const to = new Date();
    return { from: format(subDays(to, 29), 'yyyy-MM-dd'), to: format(to, 'yyyy-MM-dd') };
  }, []);

  const patient = usePatient(patientId);
  const clinics = usePatientClinics(patientId);
  const goals = useGoals(patientId, range);
  const meals = useMeals(patientId, range);
  const averagePlate = useAveragePlate(patientId, range);
  const medScore = useMediterraneanScore(patientId, range);
  const cgm = useCgmWholeSummary(patientId, range);
  // Header "details" dates — reuses the same queries PatientRail (always
  // mounted in the header) already fetches, so no extra network cost.
  const reports = usePatientReports(patientId);
  const subscriptions = useSubscriptions(patientId);
  const nutrition = useNutritionDetailsByDate(patientId, range);

  const context = useMemo(() => {
    const mealRows = meals.data ?? [];
    const mealLogging =
      mealRows.length > 0
        ? (() => {
            const kpis = computeMealTimingKpis(mealRows, 'all');
            const days = new Set<string>();
            for (const m of mealRows) if (m.meal_date) days.add(m.meal_date);
            return {
              mealsLogged: mealRows.length,
              daysWithLogs: days.size,
              avgFirstMeal: HH_MM(kpis.avgFirstMealMinutes),
              avgLastMeal: HH_MM(kpis.avgLastMealMinutes),
              mealsPerDay: kpis.mealsPerDay,
            };
          })()
        : null;

    // CGM time-in-range: target-band minutes ÷ total minutes.
    const tir = (() => {
      const t = cgm.data?.glucose_time_in_range;
      if (!t || !t.minutes_total) return null;
      const target = t.values?.find((v) => v.target_name === 'target');
      return target ? Math.round((target.minutes_in_range / t.minutes_total) * 100) : null;
    })();

    // Header "details" dates, computed the same way PatientRail does.
    const subs = subscriptions.data ?? [];
    const startDate =
      subs.length > 0
        ? ([...subs].map((s) => s.start_date).filter((d): d is string => !!d).sort()[0] ?? null)
        : patient.data?.tracking_start_date ?? null;
    const todayIso = new Date().toISOString().slice(0, 10);
    const nextReportDate =
      subs
        .filter((s) => s.start_date <= todayIso && (s.expiration_date ?? '') >= todayIso)
        .sort((a, b) => (a.expiration_date ?? '').localeCompare(b.expiration_date ?? ''))[0]
        ?.expiration_date ?? null;
    const reportList = reports.data ?? [];
    const lastReport =
      reportList.length > 0
        ? [...reportList].sort((a, b) =>
            (b.report_date ?? b.date_until).localeCompare(a.report_date ?? a.date_until),
          )[0]
        : null;
    const lastReportDate = lastReport?.report_date ?? lastReport?.date_until ?? null;

    // Per-day nutrition summary (bounded — averages, not raw logs). Each row is
    // one meal_date, so these average across the days the patient logged.
    const nutRows = (nutrition.data?.rows ?? []) as NutritionDetailsRow[];
    const caloriesPerDay = avgPerDay(nutRows, 'energy_kcal');
    const macros = {
      carbs: avgPerDay(nutRows, 'carbohydrate_g'),
      fat: avgPerDay(nutRows, 'fat_g'),
      protein: avgPerDay(nutRows, 'protein_g'),
      fiber: avgPerDay(nutRows, 'fiber_g'),
    };
    const hasMacros = Object.values(macros).some((v) => v !== null);

    return buildPatientContext({
      patientId,
      patient: patient.data
        ? {
            birthday: patient.data.birthday ?? null,
            sex: patient.data.sex ?? null,
            motivations: patient.data.motivations ?? null,
            diet_restrictions: patient.data.diet_restrictions ?? null,
          }
        : null,
      clinics: clinics.data?.map((c) => ({ name: c.name })) ?? null,
      profile: { startDate, nextReportDate, lastReportDate },
      goals:
        goals.data?.map((g) => ({
          name: g.target_display_name || g.statement,
          target: g.target_unit ? `${g.target_value} ${g.target_unit}` : String(g.target_value),
          progress:
            g.range_total_count > 0 ? `${g.range_success_count}/${g.range_total_count}` : null,
        })) ?? null,
      dateRange: range,
      mealLogging,
      dietQuality: medScore.data ? { medScore: medScore.data.total_score } : null,
      averagePlate: plateRecord(averagePlate.data),
      caloriesPerDay,
      macronutrients: hasMacros ? macros : null,
      cgm: tir !== null ? { timeInRangePct: tir } : null,
    });
  }, [
    patientId,
    range,
    patient.data,
    clinics.data,
    goals.data,
    meals.data,
    averagePlate.data,
    medScore.data,
    cgm.data,
    reports.data,
    subscriptions.data,
    nutrition.data,
  ]);

  const isLoading =
    (patient.isLoading && !patient.data) ||
    (meals.isLoading && !meals.data) ||
    (medScore.isLoading && !medScore.data);

  return { context, isLoading };
}
